import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { getProgramTemplates } from "generated/api/program-templates";
import { useCurrentClient } from "hooks/api/use-clients";
import { programTemplateKeys, normalizeProgramTemplatesParams } from "hooks/api/query-keys";

import type { PagedModelCampaignTouchpointSummaryDTO } from "generated/schemas";
import type { GetAllProgramTemplatesParams } from "generated/schemas";

const programTemplatesApi = getProgramTemplates();

export const useProgramTemplates = <TData = PagedModelCampaignTouchpointSummaryDTO>(
  params: GetAllProgramTemplatesParams = {},
  options?: Omit<
    UseQueryOptions<PagedModelCampaignTouchpointSummaryDTO, Error, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.id;

  const { enabled, ...queryOptions } = options || {};
  const normalizedParams = normalizeProgramTemplatesParams(params);

  // We exclude sort from the query key to allow local sorting without re-fetching
  const { sort, ...restParams } = normalizedParams;

  const query = useQuery({
    queryKey: programTemplateKeys.list(clientId ?? "", restParams),
    queryFn: () => {
      if (!clientId) {
        throw new Error("Client ID is required for useProgramTemplates");
      }
      return programTemplatesApi.getAllProgramTemplates({ clientId }, normalizedParams);
    },
    ...queryOptions,
    enabled: !!clientId && enabled !== false,
  });

  const pagedData = query.data as PagedModelCampaignTouchpointSummaryDTO;

  return {
    ...query,
    programTemplates: pagedData?.content || [],
    totalElements: pagedData?.page?.totalElements || 0,
  };
};
